import React, { FormEvent, useId, useState } from 'react';
import { InputTextField, Button as ComponentButton } from '@/components';
import { Button } from 'primereact/button';
import { useContainerExtractedValidations } from '@/hooks';
import classnames from 'classnames';
import { Divider } from 'primereact/divider';

import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import useToastNotification from '@/hooks/useToastNotification';
import { useMutation } from '@tanstack/react-query';
import { verifyRut } from '@/pages/CustomerRegistration/verifyRut';
import { generateANPSharedExport } from '../generateANPSharedExport';

import styles from './ANPImport.module.scss';

export interface ANPSharedExportFormState {
  container: string;
}

interface ANPSharedExportFormErrors {
  container: string;
  dispatcherTaxIds: { [key: string]: string };
}

const ANPSharedExport: React.FC = () => {
  const { t } = useTranslation('workOrder');
  const { showToast } = useToastNotification();
  const idPrefix = useId();
  const [formData, setFormData] = useState<ANPSharedExportFormState>({
    container: '',
  });

  const [dispatcherTaxIds, setDispatcherTaxIds] = useState<
    { id: string; value: string }[]
  >([
    { id: `${idPrefix}-0`, value: '' },
    { id: `${idPrefix}-1`, value: '' },
  ]);

  const [formErrors, setFormErrors] = useState<ANPSharedExportFormErrors>({
    container: '',
    dispatcherTaxIds: {},
  });

  const handleDispatcherTaxIdChange = (id: string, value: string) => {
    setDispatcherTaxIds((currentDispatcherTaxIds) =>
      currentDispatcherTaxIds.map((taxId) =>
        taxId.id === id ? { ...taxId, value } : taxId
      )
    );

    // Real-time validation
    const dispatcherTaxIdRegex = /^\d{12}$/;
    const isDuplicate = dispatcherTaxIds.some(
      (taxId) => taxId.id !== id && taxId.value === value && value.length > 0
    );

    setFormErrors((prevErrors) => {
      const newErrors = { ...prevErrors };

      if (isDuplicate) {
        newErrors.dispatcherTaxIds[id] = t('dispatcherTaxIdDuplicate');
      } else if (!dispatcherTaxIdRegex.test(value) && value.length > 0) {
        newErrors.dispatcherTaxIds[id] = t('dispatcherTaxIdInvalid');
      } else {
        newErrors.dispatcherTaxIds[id] = '';
      }

      return newErrors;
    });
  };

  const addDispatcherTaxIdInput = () => {
    if (dispatcherTaxIds.length < 6) {
      setDispatcherTaxIds((currentDispatcherTaxIds) => [
        ...currentDispatcherTaxIds,
        { id: `${idPrefix}-${currentDispatcherTaxIds.length}`, value: '' },
      ]);
    }
  };

  const { marca, numero, containerIsValid } = useContainerExtractedValidations(
    formData.container
  );

  const removeDispatcherTaxIdInput = (idToRemove: string) => {
    setDispatcherTaxIds((currentDispatcherTaxIds) =>
      currentDispatcherTaxIds.filter(({ id }) => id !== idToRemove)
    );
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      dispatcherTaxIds: Object.fromEntries(
        Object.entries(prevErrors.dispatcherTaxIds).filter(
          ([id]) => id !== idToRemove
        )
      ),
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const sanitizedValue =
      name === 'container' ? value.replace(/\//g, '') : value;
    setFormData((prevState) => ({
      ...prevState,
      [name]: sanitizedValue,
    }));
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  const validateForm = () => {
    let errors: ANPSharedExportFormErrors = { ...formErrors };
    let isValid = true;

    if (!formData.container || !containerIsValid) {
      errors = {
        ...errors,
        container:
          formData.container && !containerIsValid
            ? t('containerInvalid')
            : t('containerRequired'),
      };
      isValid = false;
    }

    const dispatcherTaxIdRegex = /^\d{12}$/;
    const rutValues = dispatcherTaxIds.map((taxId) => taxId.value);

    dispatcherTaxIds.forEach((dispatcherTaxId) => {
      if (!dispatcherTaxId.value) {
        errors.dispatcherTaxIds[dispatcherTaxId.id] = t(
          'dispatcherTaxIdRequired'
        );
        isValid = false;
      } else if (!dispatcherTaxIdRegex.test(dispatcherTaxId.value)) {
        errors.dispatcherTaxIds[dispatcherTaxId.id] = t(
          'dispatcherTaxIdInvalid'
        );
        isValid = false;
      } else {
        // Check for duplicates
        const duplicateCount = rutValues.filter(
          (rut) => rut === dispatcherTaxId.value
        ).length;
        if (duplicateCount > 1) {
          errors.dispatcherTaxIds[dispatcherTaxId.id] = t(
            'dispatcherTaxIdDuplicate'
          );
          isValid = false;
        } else {
          errors.dispatcherTaxIds[dispatcherTaxId.id] = '';
        }
      }
    });

    setFormErrors(errors);
    return isValid;
  };

  const verifyRutMutation = useMutation({
    mutationFn: verifyRut,
    onError: (error) => {
      showToast({ severity: 'error', detail: `${error}` });
    },
  });

  const generateANPSharedExportMutation = useMutation({
    mutationFn: generateANPSharedExport,
    onError: (error) => {
      if (error.toString().trim() !== 'Error') {
        showToast({ severity: 'error', detail: `${error}` });
      } else {
        showToast({
          severity: 'error',
          detail: t('eirFormValidations.unexpectedError'),
        });
      }
    },
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validateForm()) {
      const rutList = dispatcherTaxIds.map((val) => val.value);

      try {
        const validationPromises = rutList.map((rut) =>
          verifyRutMutation.mutateAsync({ rut })
        );
        const results = await Promise.all(validationPromises);
        const invalidRut = results.find(
          (result) => result?.m_Item1 === null && result?.m_Item2
        );
        if (invalidRut) {
          showToast({
            severity: 'error',
            detail: invalidRut.m_Item2,
          });
          return;
        }
        const payload = {
          dispatcherTaxId: dispatcherTaxIds.map((val) => val.value),
          marca,
          numero,
        };
        generateANPSharedExportMutation.mutate(payload);
      } catch (error) {
        /* empty */
      }
    }
  };

  return (
    <div className={styles.container}>
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className="col-12 flex align-items-start pl-0 flex-column">
          <InputTextField
            className={`col-12 md:col-5 pl-0 py-0 ${styles.inputTextShared}`}
            label={t('container')}
            id="container"
            onChange={handleInputChange}
            value={formData.container}
            name="container"
            error={formErrors.container}
            placeholder={t('containerExample')}
            longErrorMessage
          />

          <div
            className={`col-12 md:col-5 pl-0 py-0 ${styles.dispatchersContainer}`}
          >
            <strong className="mb-3 flex mt-0 pt-0">{t('dispatchers')}</strong>
            {dispatcherTaxIds.map((dispatcherTaxId, index) => (
              <div
                key={`${dispatcherTaxId.id}`}
                className="flex col-12 align-items-start flex-row px-0 mx-0 my-0 py-0"
              >
                <InputTextField
                  className={`col-10 md:col-5  px-0 mx-0  pt-0 ${styles.inputTextSharedExport} ${formErrors.dispatcherTaxIds[dispatcherTaxId.id] ? 'mt-2' : 'mt-0'}`}
                  id="dispatcherTaxId"
                  name="dispatcherTaxId"
                  error={formErrors.dispatcherTaxIds[dispatcherTaxId.id]}
                  placeholder={t('dispatcherTaxIdPlaceholder')}
                  value={dispatcherTaxId.value}
                  onChange={(e) =>
                    handleDispatcherTaxIdChange(
                      dispatcherTaxId.id,
                      e.target.value
                    )
                  }
                  longErrorMessage
                >
                  {dispatcherTaxIds.length > 1 && index !== 0 && (
                    <Button
                      onClick={() =>
                        removeDispatcherTaxIdInput(dispatcherTaxId.id)
                      }
                      className="p-button-danger p-button-outlined align-items-center justify-content-center ml-3 px-3"
                      severity="danger"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  )}
                </InputTextField>
              </div>
            ))}
            {dispatcherTaxIds.length < 6 && (
              <ComponentButton
                onClick={addDispatcherTaxIdInput}
                secondary
                className={`mt-3 ${styles.addButton}`}
              >
                <FontAwesomeIcon
                  icon={faPlus}
                  className={styles.addButtonIcon}
                />
                <span>{t('addDispatcher')}</span>
              </ComponentButton>
            )}
          </div>

          <Divider />
          <Button
            label={t('getOrderButton')}
            type="submit"
            className={classnames(styles.buttonOrder)}
            disabled={
              verifyRutMutation.isPending ||
              generateANPSharedExportMutation.isPending
            }
            loading={
              verifyRutMutation.isPending ||
              generateANPSharedExportMutation.isPending
            }
          />
        </div>
      </form>
      {generateANPSharedExportMutation.data && (
        <div
          className={classnames(
            styles.orderConfirmation,
            generateANPSharedExportMutation.data?.ok === false && styles.errorBg
          )}
        >
          <FontAwesomeIcon
            icon={generateANPSharedExportMutation.data?.ok ? faCheck : faTimes}
            size="xl"
            className={styles.iconCheck}
          />
          <div style={{ wordWrap: 'break-word', maxWidth: '100%', flex: 1 }}>
            <strong style={{ display: 'block', lineHeight: '1.4' }}>
              {generateANPSharedExportMutation.data?.mensaje ||
                'No message available'}
            </strong>
          </div>
        </div>
      )}
    </div>
  );
};
export default ANPSharedExport;
